<script>
  import api from '$lib/api.js';
  import { toast } from '$lib/toast.svelte.js';
  import { authStore } from '../stores/authStore.svelte.js';
  import PageHeader from '../components/PageHeader.svelte';

  let firstName = $state('');
  let lastName = $state('');
  let savingProfile = $state(false);

  let currentPassword = $state('');
  let newPassword = $state('');
  let confirmPassword = $state('');
  let savingPassword = $state(false);

  $effect(() => {
    const user = authStore.user;
    if (user) {
      firstName = user.first_name ?? '';
      lastName = user.last_name ?? '';
    }
  });

  const strength = $derived({
    length: newPassword.length >= 8,
    upper: /[A-Z]/.test(newPassword),
    lower: /[a-z]/.test(newPassword),
    digit: /\d/.test(newPassword),
  });
  const passwordOk = $derived(
    Object.values(strength).every(Boolean) && newPassword === confirmPassword
  );

  async function saveProfile(event) {
    event.preventDefault();
    savingProfile = true;
    try {
      const updated = await api.updateProfile({ first_name: firstName, last_name: lastName });
      authStore.setAuth({ ...authStore.user, ...updated }, authStore.token);
      toast.success('Profile updated');
    } catch (e) {
      toast.error(e.message);
    } finally {
      savingProfile = false;
    }
  }

  async function savePassword(event) {
    event.preventDefault();
    savingPassword = true;
    try {
      await api.changePassword(currentPassword, newPassword);
      toast.success('Password changed');
      currentPassword = newPassword = confirmPassword = '';
    } catch (e) {
      toast.error(e.message);
    } finally {
      savingPassword = false;
    }
  }

  const roleLabels = {
    super_user: 'Super user',
    pm_staff: 'Property management staff',
    property_owner: 'Property owner',
  };
</script>

<PageHeader title="Settings" subtitle="Your account." />

<div class="grid gap-6 lg:grid-cols-2">
  <form class="p-card space-y-4 p-5" onsubmit={saveProfile}>
    <h2 class="text-sm font-semibold">Profile</h2>
    <div>
      <p class="text-xs p-muted">Email</p>
      <p class="mt-1 text-sm">{authStore.context?.email ?? authStore.user?.email ?? '—'}</p>
    </div>
    <div>
      <p class="text-xs p-muted">Access level</p>
      <p class="mt-1 text-sm">{roleLabels[authStore.role] ?? '—'}</p>
    </div>
    <div class="grid gap-3 sm:grid-cols-2">
      <div>
        <label for="first" class="text-xs font-medium p-muted">First name</label>
        <input id="first" class="p-input mt-1" bind:value={firstName} />
      </div>
      <div>
        <label for="last" class="text-xs font-medium p-muted">Last name</label>
        <input id="last" class="p-input mt-1" bind:value={lastName} />
      </div>
    </div>
    <button type="submit" class="p-btn p-btn-primary" disabled={savingProfile}>Save profile</button>
  </form>

  <form class="p-card space-y-4 p-5" onsubmit={savePassword}>
    <h2 class="text-sm font-semibold">Password</h2>
    <div>
      <label for="cur" class="text-xs font-medium p-muted">Current password</label>
      <input id="cur" class="p-input mt-1" type="password" bind:value={currentPassword}
             autocomplete="current-password" />
    </div>
    <div>
      <label for="new" class="text-xs font-medium p-muted">New password</label>
      <input id="new" class="p-input mt-1" type="password" bind:value={newPassword}
             autocomplete="new-password" />
    </div>
    <div>
      <label for="conf" class="text-xs font-medium p-muted">Confirm new password</label>
      <input id="conf" class="p-input mt-1" type="password" bind:value={confirmPassword}
             autocomplete="new-password" />
    </div>
    <ul class="space-y-1 text-xs p-muted">
      <li>{strength.length ? '✓' : '·'} At least 8 characters</li>
      <li>{strength.upper ? '✓' : '·'} An uppercase letter</li>
      <li>{strength.lower ? '✓' : '·'} A lowercase letter</li>
      <li>{strength.digit ? '✓' : '·'} A number</li>
    </ul>
    <button type="submit" class="p-btn p-btn-primary" disabled={savingPassword || !passwordOk}>
      Change password
    </button>
  </form>
</div>
